import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';
export const DELIVERY_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const DELIVERY_WINDOWS_MAX = 28;
export const deliveryWeekdaySchema = z.number().int().min(1).max(7);
const localTime = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'deliveryPlanning.errors.time');
export const WEEKLY_SCHEDULE_STORAGE_MAX_LENGTH = 8192;
/** Reusable local weekday windows, shared by delivery planning and custom fields. */
export const weeklyScheduleSchema = z
    .array(z
    .object({
    weekdays: z.array(deliveryWeekdaySchema).min(1).max(7),
    from: localTime,
    until: localTime,
})
    .strict())
    .max(DELIVERY_WINDOWS_MAX)
    .superRefine((windows, ctx) => {
    const issue = (path, key) => ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `deliveryPlanning.errors.${key}`,
    });
    windows.forEach((window, index) => {
        if (new Set(window.weekdays).size !== window.weekdays.length)
            issue([index, 'weekdays'], 'duplicate');
        if (window.until <= window.from)
            issue([index, 'until'], 'overnight');
        if (windows
            .slice(0, index)
            .some((previous) => previous.weekdays.some((day) => window.weekdays.includes(day)) &&
            window.from < previous.until &&
            previous.from < window.until))
            issue([index], 'overlap');
    });
});
/** Recurring wall-clock times at the delivery location; never UTC instants. */
export const deliveryPlanningSchema = z
    .object({
    enabled: z.boolean(),
    minRemainingShelfLifeDays: z.number().int().min(0).max(3650).nullish(),
    warehouseCode: z.string().trim().min(1).max(20).nullish(),
    receivingWindows: weeklyScheduleSchema,
    transportBasis: z.enum(['ORDER', 'SHIPMENT']),
    transportRules: z
        .array(z
        .object({
        sourceWeekday: deliveryWeekdaySchema,
        deliveryWeekday: deliveryWeekdaySchema,
    })
        .strict())
        .max(7),
})
    .strict()
    .superRefine((plan, ctx) => {
    const issue = (path, key) => ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `deliveryPlanning.errors.${key}`,
    });
    const sources = plan.transportRules.map((rule) => rule.sourceWeekday);
    if (new Set(sources).size !== sources.length)
        issue(['transportRules'], 'sourceDuplicate');
});
export const deliveryPlanningSnapshotSchema = z
    .object({
    schemaVersion: z.literal(1),
    plan: deliveryPlanningSchema,
    customized: z.boolean(),
    source: z
        .object({
        customerId: idSchema,
        addressId: idSchema.nullable(),
        addressLabel: z.string().nullable(),
        addressUpdatedAt: isoDateTimeSchema.nullable(),
        capturedAt: isoDateTimeSchema,
        inheritedFromDocumentId: idSchema.nullable(),
    })
        .strict(),
})
    .strict();
/** Provenance is server-owned. Clients can only request a deliberate change. */
export const deliveryPlanningChangeSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('OVERRIDE'), plan: deliveryPlanningSchema }).strict(),
    z.object({ action: z.literal('REFRESH') }).strict(),
    z.object({ action: z.literal('KEEP') }).strict(),
]);
export function emptyDeliveryPlanning() {
    return { enabled: false, receivingWindows: [], transportBasis: 'ORDER', transportRules: [] };
}
//# sourceMappingURL=delivery-planning.js.map